import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader2,
  ArrowLeft,
  Upload,
  Play,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  Check,
  X,
  Search,
  ChevronRight,
  FileText,
  RefreshCw,
  Edit3,
  Save,
  Image as ImageIcon
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Use cookie-based auth - credentials: 'include' sends session cookie
const getAuthHeaders = () => {
  return {
    'Content-Type': 'application/json'
  };
};

// Helper to make authenticated fetch requests
const authFetch = (url, options = {}) => {
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  });
};

// Status badge colors
const STATUS_COLORS = {
  uploaded: 'bg-gray-100 text-gray-700',
  parsing: 'bg-blue-100 text-blue-700',
  validated: 'bg-green-100 text-green-700',
  review_required: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-teal-100 text-teal-700',
  publishing: 'bg-purple-100 text-purple-700',
  published: 'bg-green-200 text-green-800',
  failed: 'bg-red-100 text-red-700',
  canceled: 'bg-gray-200 text-gray-600'
};

const VALIDATION_COLORS = {
  valid: 'bg-green-100 text-green-700',
  warning: 'bg-yellow-100 text-yellow-700',
  error: 'bg-red-100 text-red-700'
};

const APPROVAL_COLORS = {
  pending: 'bg-gray-100 text-gray-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700'
};

const StatusBadge = ({ status, colors }) => (
  <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
    {status?.replace('_', ' ')}
  </span>
);

const ImportDetailPage = ({ importId, onBack, userRole = 'admin' }) => {
  const [importData, setImportData] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Only admin can approve/publish
  const canApprovePublish = userRole === 'admin';
  
  // Item filters
  const [validationFilter, setValidationFilter] = useState('');
  const [approvalFilter, setApprovalFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  // Selection
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Review drawer
  const [selectedItem, setSelectedItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  
  // Categories and product types for dropdown
  const [categories, setCategories] = useState([]);
  const [productTypes, setProductTypes] = useState([]);

  const fetchImport = useCallback(async () => {
    try {
      const resp = await authFetch(`${API_URL}/api/catalog-imports/admin/imports/${importId}`);
      if (!resp.ok) throw new Error('Failed to fetch import');
      const data = await resp.json();
      setImportData(data.import);
    } catch (err) {
      setError(err.message);
    }
  }, [importId]);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (validationFilter) params.append('validation_status', validationFilter);
      if (approvalFilter) params.append('approval_status', approvalFilter);
      params.append('page', page.toString());
      params.append('page_size', '20');

      const resp = await authFetch(`${API_URL}/api/catalog-imports/admin/imports/${importId}/items?${params}`);
      
      if (!resp.ok) throw new Error('Failed to fetch items');
      
      const data = await resp.json();
      setItems(data.items || []);
      setTotalItems(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [importId, validationFilter, approvalFilter, page]);

  const fetchMetadata = async () => {
    try {
      // Fetch categories
      const catResp = await authFetch(`${API_URL}/api/categories?limit=500`);
      if (catResp.ok) {
        const catData = await catResp.json();
        setCategories(catData.categories || []);
      }
      
      // Fetch product types
      const ptResp = await authFetch(`${API_URL}/api/product-types`);
      if (ptResp.ok) {
        const ptData = await ptResp.json();
        setProductTypes(ptData.product_types || []);
      }
    } catch (err) {
      console.error('Failed to fetch metadata:', err);
    }
  };

  useEffect(() => {
    fetchImport();
    fetchMetadata();
  }, [fetchImport]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleAction = async (action, body = {}) => {
    try {
      setActionLoading(true);
      setError(null);
      
      const resp = await authFetch(`${API_URL}/api/catalog-imports/admin/imports/${importId}/${action}`, {
        method: 'POST',
        body: JSON.stringify(body)
      });
      
      if (!resp.ok) {
        const errData = await resp.json();
        throw new Error(errData.detail || `Action ${action} failed`);
      }
      
      const data = await resp.json();
      showSuccess(data.message || `${action} completed`);
      
      // Refresh data
      await fetchImport();
      await fetchItems();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkApprove = async () => {
    const itemIds = selectedItems.size > 0 ? Array.from(selectedItems) : null;
    await handleAction('items/bulk-approve', { 
      item_ids: itemIds,
      filter: itemIds ? null : { validation_status: validationFilter || undefined }
    });
    setSelectedItems(new Set());
    setSelectAll(false);
  };

  const handleBulkReject = async () => {
    if (selectedItems.size === 0) {
      setError('Select items to reject');
      return;
    }
    await handleAction('items/bulk-reject', { 
      item_ids: Array.from(selectedItems),
      note: 'Bulk rejected'
    });
    setSelectedItems(new Set());
    setSelectAll(false);
  };

  const handleUpdateItem = async (itemId, updates) => {
    try {
      setActionLoading(true);
      const resp = await authFetch(`${API_URL}/api/catalog-imports/admin/import-items/${itemId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });
      
      if (!resp.ok) {
        const errData = await resp.json();
        throw new Error(errData.detail || 'Failed to update item');
      }
      
      showSuccess('Item updated');
      setEditingItem(null);
      await fetchItems();
      
      // Update selected item if viewing
      if (selectedItem?.id === itemId) {
        const data = await resp.json();
        setSelectedItem(data.item);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleExportErrors = () => {
    window.open(`${API_URL}/api/catalog-imports/admin/imports/${importId}/errors/export`, '_blank');
  };

  const handleExportWarnings = () => {
    window.open(`${API_URL}/api/catalog-imports/admin/imports/${importId}/warnings/export`, '_blank');
  };

  const handleDownloadImages = async () => {
    try {
      setActionLoading(true);
      setError(null);
      
      const resp = await authFetch(`${API_URL}/api/catalog-imports/admin/imports/${importId}/download-images`, {
        method: 'POST'
      });
      
      if (!resp.ok) {
        const errData = await resp.json();
        throw new Error(errData.detail || 'Failed to download images');
      }
      
      const data = await resp.json();
      showSuccess(`Downloaded ${data.summary.successful_downloads} images (${data.summary.failed_downloads} failed)`);
      
      // Refresh data
      await fetchImport();
      await fetchItems();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleSelectItem = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map(i => i.id)));
    }
    setSelectAll(!selectAll);
  };

  if (!importData && loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
      </div>
    );
  }

  const canParse = importData?.status === 'uploaded' && importData?.file_name;
  const canValidate = ['validated', 'review_required'].includes(importData?.status);
  const canDownloadImages = ['validated', 'review_required', 'approved'].includes(importData?.status);
  const canApprove = ['validated', 'review_required'].includes(importData?.status);
  const canPublish = importData?.status === 'approved';
  const canCancel = !['published', 'canceled'].includes(importData?.status);

  return (
    <div className="space-y-6" data-testid="import-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-heading font-semibold text-ivory-800">
              Import: {importData?.id}
            </h2>
            <StatusBadge status={importData?.status} colors={STATUS_COLORS} />
          </div>
          <p className="text-sm text-ivory-500">
            {importData?.vendor_name} ({importData?.vendor_code}) • {importData?.file_name || 'No file uploaded'}
          </p>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          {successMessage}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <XCircle className="w-4 h-4" />
          {error}
          <Button variant="ghost" size="sm" onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg border border-ivory-200 p-4">
          <p className="text-sm text-ivory-500">Total Rows</p>
          <p className="text-2xl font-semibold text-ivory-800">{importData?.stats?.total_rows || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-ivory-200 p-4">
          <p className="text-sm text-green-600">Valid</p>
          <p className="text-2xl font-semibold text-green-700">{importData?.stats?.valid_rows || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-ivory-200 p-4">
          <p className="text-sm text-yellow-600">Warnings</p>
          <p className="text-2xl font-semibold text-yellow-700">{importData?.stats?.warning_rows || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-ivory-200 p-4">
          <p className="text-sm text-red-600">Errors</p>
          <p className="text-2xl font-semibold text-red-700">{importData?.stats?.error_rows || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-ivory-200 p-4">
          <p className="text-sm text-purple-600">Published</p>
          <p className="text-2xl font-semibold text-purple-700">{importData?.stats?.published_rows || 0}</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-white rounded-lg border border-ivory-200 p-4">
        <div className="flex flex-wrap gap-3">
          {canParse && (
            <Button 
              onClick={() => handleAction('parse')} 
              disabled={actionLoading}
              data-testid="parse-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
              Parse File
            </Button>
          )}
          {canValidate && (
            <Button 
              variant="outline" 
              onClick={() => handleAction('validate')} 
              disabled={actionLoading}
              data-testid="validate-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Re-Validate
            </Button>
          )}
          {canDownloadImages && (
            <Button 
              variant="outline" 
              onClick={handleDownloadImages} 
              disabled={actionLoading}
              className="text-blue-600 border-blue-300 hover:bg-blue-50"
              data-testid="download-images-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ImageIcon className="w-4 h-4 mr-2" />}
              Download Images
            </Button>
          )}
          {canApprove && canApprovePublish && (
            <Button 
              onClick={() => handleAction('approve')} 
              disabled={actionLoading}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="approve-import-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Approve Import
            </Button>
          )}
          {canPublish && canApprovePublish && (
            <Button 
              onClick={() => handleAction('publish')} 
              disabled={actionLoading}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="publish-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Publish to Products
            </Button>
          )}
          {canCancel && (
            <Button 
              variant="outline" 
              onClick={() => handleAction('cancel')} 
              disabled={actionLoading}
              className="text-red-600 border-red-300 hover:bg-red-50"
              data-testid="cancel-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel Import
            </Button>
          )}
          
          <div className="flex-1" />
          
          {importData?.stats?.error_rows > 0 && (
            <Button variant="outline" size="sm" onClick={handleExportErrors}>
              <Download className="w-4 h-4 mr-1" />
              Export Errors
            </Button>
          )}
          {importData?.stats?.warning_rows > 0 && (
            <Button variant="outline" size="sm" onClick={handleExportWarnings}>
              <Download className="w-4 h-4 mr-1" />
              Export Warnings
            </Button>
          )}
        </div>
      </div>

      {/* Top Errors */}
      {importData?.error_summary?.top_errors?.length > 0 && (
        <div className="bg-red-50 rounded-lg border border-red-200 p-4">
          <h3 className="text-sm font-semibold text-red-700 mb-2">Top Errors</h3>
          <div className="flex flex-wrap gap-2">
            {importData.error_summary.top_errors.map((err, idx) => (
              <span key={idx} className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                {err.code}: {err.count}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Items Section */}
      <div className="bg-white rounded-xl border border-ivory-200 overflow-hidden">
        {/* Items Header */}
        <div className="px-4 py-3 border-b border-ivory-200 bg-ivory-50">
          <div className="flex flex-wrap items-center gap-4">
            <h3 className="font-semibold text-ivory-800">Items ({totalItems})</h3>
            
            <select
              value={validationFilter}
              onChange={(e) => { setValidationFilter(e.target.value); setPage(1); }}
              className="px-2 py-1 border border-ivory-300 rounded text-sm"
            >
              <option value="">All Validation</option>
              <option value="valid">Valid</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
            </select>
            
            <select
              value={approvalFilter}
              onChange={(e) => { setApprovalFilter(e.target.value); setPage(1); }}
              className="px-2 py-1 border border-ivory-300 rounded text-sm"
            >
              <option value="">All Approval</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            
            <div className="flex-1" />
            
            {selectedItems.size > 0 && (
              <span className="text-sm text-ivory-600">{selectedItems.size} selected</span>
            )}
            
            {canApprovePublish && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleBulkApprove}
                disabled={actionLoading}
                className="text-green-600"
                data-testid="bulk-approve-btn"
              >
                <Check className="w-4 h-4 mr-1" />
                Bulk Approve
              </Button>
            )}
            {canApprovePublish && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleBulkReject}
                disabled={actionLoading || selectedItems.size === 0}
                className="text-red-600"
                data-testid="bulk-reject-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Bulk Reject
              </Button>
            )}
          </div>
        </div>

        {/* Items Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ivory-50 border-b border-ivory-200">
              <tr>
                <th className="px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={toggleSelectAll}
                    className="rounded"
                  />
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-ivory-600">Row</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-ivory-600">Vendor SKU</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-ivory-600">Title</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-ivory-600">Validation</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-ivory-600">Approval</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-ivory-600">Match</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-ivory-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ivory-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-gold-500" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-ivory-500">
                    No items found
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr 
                    key={item.id} 
                    className={`hover:bg-ivory-50 ${selectedItems.has(item.id) ? 'bg-blue-50' : ''}`}
                    data-testid={`item-row-${item.id}`}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item.id)}
                        onChange={() => toggleSelectItem(item.id)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-3 py-2 text-ivory-600">{item.row_number}</td>
                    <td className="px-3 py-2 font-mono text-xs">{item.normalized?.vendor_sku || '-'}</td>
                    <td className="px-3 py-2 max-w-[200px] truncate" title={item.normalized?.title}>
                      {item.normalized?.title || '-'}
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={item.validation?.status} colors={VALIDATION_COLORS} />
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={item.approval?.status} colors={APPROVAL_COLORS} />
                    </td>
                    <td className="px-3 py-2 text-xs text-ivory-500">
                      {item.match?.mode === 'none' ? '-' : item.match?.mode}
                    </td>
                    <td className="px-3 py-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedItem(item)}
                      >
                        <Edit3 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-ivory-200 flex items-center justify-between">
            <p className="text-sm text-ivory-500">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Review Drawer */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={() => setSelectedItem(null)}>
          <div 
            className="w-full max-w-xl bg-white h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-ivory-200 px-6 py-4 flex items-center justify-between">
              <h3 className="font-semibold text-ivory-800">Review Item #{selectedItem.row_number}</h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedItem(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Status Badges */}
              <div className="flex gap-2">
                <StatusBadge status={selectedItem.validation?.status} colors={VALIDATION_COLORS} />
                <StatusBadge status={selectedItem.approval?.status} colors={APPROVAL_COLORS} />
                {selectedItem.publish?.status !== 'not_published' && (
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                    {selectedItem.publish?.status}
                  </span>
                )}
              </div>

              {/* Validation Errors/Warnings */}
              {(selectedItem.validation?.errors?.length > 0 || selectedItem.validation?.warnings?.length > 0) && (
                <div className="space-y-2">
                  {selectedItem.validation.errors.map((err, idx) => (
                    <div key={idx} className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                      <strong>{err.code}</strong>: {err.message}
                    </div>
                  ))}
                  {selectedItem.validation.warnings.map((warn, idx) => (
                    <div key={idx} className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-3 py-2 rounded text-sm">
                      <strong>{warn.code}</strong>: {warn.message}
                    </div>
                  ))}
                </div>
              )}

              {/* Normalized Fields */}
              <div>
                <h4 className="font-semibold text-ivory-700 mb-3">Normalized Fields</h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-ivory-500">Vendor SKU</label>
                    <Input value={selectedItem.normalized?.vendor_sku || ''} disabled className="bg-ivory-50" />
                  </div>
                  <div>
                    <label className="text-xs text-ivory-500">Title</label>
                    <Input 
                      value={editingItem?.title ?? (selectedItem.normalized?.title || '')} 
                      onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-ivory-500">Description</label>
                    <textarea 
                      className="w-full px-3 py-2 border border-ivory-300 rounded-lg text-sm"
                      rows={3}
                      value={editingItem?.description ?? (selectedItem.normalized?.description || '')}
                      onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-ivory-500">Product Type</label>
                      <select
                        className="w-full px-3 py-2 border border-ivory-300 rounded-lg text-sm"
                        value={editingItem?.product_type_id ?? (selectedItem.normalized?.product_type_id || '')}
                        onChange={(e) => setEditingItem({ ...editingItem, product_type_id: e.target.value })}
                      >
                        <option value="">Select...</option>
                        {productTypes.map(pt => (
                          <option key={pt.id} value={pt.id}>{pt.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-ivory-500">Category</label>
                      <select
                        className="w-full px-3 py-2 border border-ivory-300 rounded-lg text-sm"
                        value={editingItem?.category_id ?? (selectedItem.normalized?.category_id || '')}
                        onChange={(e) => setEditingItem({ ...editingItem, category_id: e.target.value })}
                      >
                        <option value="">Select...</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-ivory-500">Cost Price</label>
                      <Input 
                        type="number"
                        step="0.01"
                        value={editingItem?.cost_price ?? (selectedItem.normalized?.cost_price || '')} 
                        onChange={(e) => setEditingItem({ ...editingItem, cost_price: parseFloat(e.target.value) || null })}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-ivory-500">MSRP</label>
                      <Input 
                        type="number"
                        step="0.01"
                        value={editingItem?.msrp ?? (selectedItem.normalized?.msrp || '')} 
                        onChange={(e) => setEditingItem({ ...editingItem, msrp: parseFloat(e.target.value) || null })}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Images */}
              {(selectedItem.images?.source_urls?.length > 0 || selectedItem.images?.downloaded?.length > 0) && (
                <div>
                  <h4 className="font-semibold text-ivory-700 mb-3">Images</h4>
                  
                  {/* Downloaded Images with previews */}
                  {selectedItem.images?.downloaded?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-ivory-500 mb-2">Downloaded Images (click to set as primary)</p>
                      <div className="grid grid-cols-3 gap-2">
                        {selectedItem.images.downloaded.map((img, idx) => (
                          <div 
                            key={idx} 
                            className={`relative border rounded overflow-hidden cursor-pointer transition-all ${
                              img.status === 'ok' 
                                ? selectedItem.images.primary_stored_url === img.stored_url
                                  ? 'border-gold-500 ring-2 ring-gold-200'
                                  : 'border-ivory-200 hover:border-gold-300'
                                : 'border-red-200 bg-red-50'
                            }`}
                            onClick={() => {
                              if (img.status === 'ok') {
                                setEditingItem({ ...editingItem, primary_image_url: img.stored_url });
                              }
                            }}
                          >
                            {img.status === 'ok' && img.stored_url ? (
                              <>
                                <img 
                                  src={`${API_URL}${img.stored_url}`} 
                                  alt={`Image ${idx + 1}`}
                                  className="w-full h-24 object-cover"
                                />
                                {selectedItem.images.primary_stored_url === img.stored_url && (
                                  <div className="absolute top-1 right-1 bg-gold-500 text-white text-xs px-1 rounded">
                                    Primary
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="h-24 flex items-center justify-center text-red-500 text-xs p-2 text-center">
                                <span>{img.message || 'Failed'}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Source URLs (if not downloaded yet) */}
                  {selectedItem.images?.downloaded?.length === 0 && selectedItem.images?.source_urls?.length > 0 && (
                    <div className="grid grid-cols-1 gap-2">
                      <p className="text-xs text-ivory-500">Source URLs (not downloaded yet)</p>
                      {selectedItem.images.source_urls.map((url, idx) => (
                        <div key={idx} className="border border-ivory-200 rounded p-2 text-xs break-all text-ivory-600">
                          {url}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Raw Data */}
              <div>
                <h4 className="font-semibold text-ivory-700 mb-3">Raw Data</h4>
                <pre className="bg-ivory-50 p-3 rounded text-xs overflow-x-auto">
                  {JSON.stringify(selectedItem.raw, null, 2)}
                </pre>
              </div>

              {/* Save Button */}
              {editingItem && Object.keys(editingItem).length > 0 && (
                <div className="sticky bottom-0 bg-white border-t border-ivory-200 -mx-6 px-6 py-4">
                  <Button 
                    onClick={() => handleUpdateItem(selectedItem.id, editingItem)}
                    disabled={actionLoading}
                    className="w-full"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportDetailPage;
